import type { FC, ReactNode } from 'react'
import type { Product } from '@/types'

import { useId } from 'react'
import { cn } from '@/lib/utils'

type GrainLength = 'long' | 'regular' | 'broken'

type GrainTone = 'white' | 'golden'

type GrainBackdrop = 'sand' | 'sage' | 'amber' | 'stone' | 'forest'

export type GrainStyle = {
    length: GrainLength
    tone: GrainTone
    backdrop: GrainBackdrop
}

interface GrainArtProps extends Partial<GrainStyle> {
    seed: string
    className?: string
}

type Grain = {
    x: number
    y: number
    angle: number
    length: number
}

const WIDTH = 400

const HEIGHT = 300

const BACKDROPS: Record<GrainBackdrop, [string, string]> = {
    sand: ['#F5EEDF', '#E2D3B4'],
    sage: ['#EDF1E7', '#CFD9C6'],
    amber: ['#F6EAD0', '#E3C88F'],
    stone: ['#F2EFE9', '#D6CEBF'],
    forest: ['#2B5B42', '#163524']
}

const TONES: Record<GrainTone, { fill: string, edge: string, shine: string }> = {
    white: { fill: '#FFFCF3', edge: '#CFC1A1', shine: '#FFFFFF' },
    golden: { fill: '#EDC57F', edge: '#B07D36', shine: '#F8DFAE' }
}

const LAYOUTS: Record<GrainLength, { columns: number, rows: number, width: number, minLength: number, maxLength: number, skip: number }> = {
    long: { columns: 8, rows: 6, width: 4.3, minLength: 14, maxLength: 16.5, skip: 0.12 },
    regular: { columns: 9, rows: 7, width: 4.8, minLength: 9.5, maxLength: 12, skip: 0.1 },
    broken: { columns: 13, rows: 9, width: 4.2, minLength: 3, maxLength: 7.5, skip: 0.15 }
}

const hashString = (value: string): number => {
    let hash = 2166136261

    for (let index = 0; index < value.length; index++) {
        hash ^= value.charCodeAt(index)
        hash = Math.imul(hash, 16777619)
    }

    return hash >>> 0
}

const createRandom = (seed: number): (() => number) => {
    let state = seed

    return () => {
        state = (state + 0x6D2B79F5) | 0

        let result = Math.imul(state ^ (state >>> 15), 1 | state)
        result = (result + Math.imul(result ^ (result >>> 7), 61 | result)) ^ result

        return ((result ^ (result >>> 14)) >>> 0) / 4294967296
    }
}

const round = (value: number): number => Math.round(value * 100) / 100

const buildGrains = (seed: string, length: GrainLength): Grain[] => {
    const layout = LAYOUTS[length]
    const random = createRandom(hashString(`${seed}:${length}`))
    const cellWidth = WIDTH / layout.columns
    const cellHeight = HEIGHT / layout.rows
    const grains: Grain[] = []

    for (let row = 0; row < layout.rows; row++) {
        for (let column = 0; column < layout.columns; column++) {
            const skip = random() < layout.skip
            const x = (column + 0.5) * cellWidth + (random() - 0.5) * cellWidth * 0.7
            const y = (row + 0.5) * cellHeight + (random() - 0.5) * cellHeight * 0.7
            const angle = random() * 180
            const grainLength = layout.minLength + random() * (layout.maxLength - layout.minLength)

            if (skip) continue

            grains.push({
                x: round(x),
                y: round(y),
                angle: round(angle),
                length: round(grainLength)
            })
        }
    }

    return grains
}

/**
 * Picks a grain illustration for a rice variety that has no photo yet.
 */
export const getGrainStyle = (product: Pick<Product, 'name' | 'processing' | 'category_slug'>): GrainStyle => {
    const name = product.name.toLowerCase()
    const processing = (product.processing ?? '').toLowerCase()
    const isGolden = name.includes('sella') || name.includes('parboil') || processing.startsWith('parboil')
    const tone: GrainTone = isGolden ? 'golden' : 'white'

    switch (product.category_slug) {
        case 'basmati':
            return { length: 'long', tone, backdrop: 'sand' }
        case 'non-basmati':
            return { length: 'regular', tone, backdrop: 'sage' }
        case 'parboiled':
            return { length: 'regular', tone, backdrop: 'stone' }
        case 'broken-rice':
            return { length: 'broken', tone, backdrop: 'amber' }
        default:
            return { length: 'regular', tone, backdrop: 'sand' }
    }
}

export const GrainArt: FC<GrainArtProps> = ({
    seed,
    length = 'regular',
    tone = 'white',
    backdrop = 'sand',
    className
}): ReactNode => {
    const id = `grain-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`
    const grains = buildGrains(seed, length)
    const colors = TONES[tone]
    const layout = LAYOUTS[length]
    const [from, to] = BACKDROPS[backdrop]
    const showShine = length !== 'broken'

    return (
        <svg
            viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
            preserveAspectRatio='xMidYMid slice'
            className={cn('block', className)}
            aria-hidden='true'
            focusable='false'
        >
            <defs>
                <linearGradient id={`${id}-bg`} x1='0' y1='0' x2='1' y2='1'>
                    <stop offset='0' stopColor={from} />
                    <stop offset='1' stopColor={to} />
                </linearGradient>

                <radialGradient id={`${id}-glow`} cx='0.3' cy='0.2' r='0.9'>
                    <stop offset='0' stopColor='#FFFFFF' stopOpacity={backdrop === 'forest' ? 0.12 : 0.45} />
                    <stop offset='1' stopColor='#FFFFFF' stopOpacity='0' />
                </radialGradient>
            </defs>

            <rect width={WIDTH} height={HEIGHT} fill={`url(#${id}-bg)`} />
            <rect width={WIDTH} height={HEIGHT} fill={`url(#${id}-glow)`} />

            {grains.map((grain, index) => (
                <g key={index} transform={`translate(${grain.x} ${grain.y}) rotate(${grain.angle})`}>
                    <ellipse
                        cx='1.4'
                        cy='1.8'
                        rx={layout.width}
                        ry={grain.length}
                        fill='#2E220F'
                        opacity={backdrop === 'forest' ? 0.3 : 0.1}
                    />

                    <ellipse
                        rx={layout.width}
                        ry={grain.length}
                        fill={colors.fill}
                        stroke={colors.edge}
                        strokeWidth='0.9'
                    />

                    {showShine && (
                        <ellipse
                            cx={round(-layout.width * 0.3)}
                            cy={round(-grain.length * 0.15)}
                            rx={round(layout.width * 0.32)}
                            ry={round(grain.length * 0.55)}
                            fill={colors.shine}
                            opacity='0.75'
                        />
                    )}
                </g>
            ))}
        </svg>
    )
}
